import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, CheckCircle, Circle } from "@phosphor-icons/react";

const RULES = [
  { key: "len", label: "Mindestens 10 Zeichen", test: (v) => v.length >= 10 },
  { key: "upper", label: "Ein Großbuchstabe", test: (v) => /[A-Z]/.test(v) },
  { key: "lower", label: "Ein Kleinbuchstabe", test: (v) => /[a-z]/.test(v) },
  { key: "digit", label: "Eine Ziffer", test: (v) => /\d/.test(v) },
];

export default function PasswortSetzen() {
  const { user, setInitialPassword, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const allOk = RULES.every((r) => r.test(pw));
  const match = pw && pw === pw2;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allOk) { setError("Das neue Passwort erfüllt nicht alle Anforderungen."); return; }
    if (!match) { setError("Die Passwörter stimmen nicht überein."); return; }
    setLoading(true);
    try {
      await setInitialPassword(current, pw);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Passwort konnte nicht gesetzt werden.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-md animate-fade-in" data-testid="set-password-page">
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary text-primary-foreground">
            <ShieldCheck size={24} weight="bold" />
          </div>
          <div>
            <div className="font-heading text-lg font-black tracking-tight">Passwort festlegen</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground">Erster Login</div>
          </div>
        </div>
        <h1 className="font-heading text-3xl font-black tracking-tighter">Willkommen{user?.name ? `, ${user.name}` : ""}!</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Aus Sicherheitsgründen müssen Sie beim ersten Login Ihr initiales Passwort durch ein eigenes ersetzen.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Initiales Passwort (aus E-Mail)</Label>
            <Input type="password" data-testid="set-password-current" value={current}
              onChange={(e) => setCurrent(e.target.value)} required className="h-12" autoComplete="current-password" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Neues Passwort</Label>
            <Input type="password" data-testid="set-password-new" value={pw}
              onChange={(e) => setPw(e.target.value)} required className="h-12" autoComplete="new-password" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground">Neues Passwort bestätigen</Label>
            <Input type="password" data-testid="set-password-confirm" value={pw2}
              onChange={(e) => setPw2(e.target.value)} required className="h-12" autoComplete="new-password" />
          </div>

          <ul className="space-y-1.5 rounded-md border border-border bg-muted/40 p-4">
            {RULES.map((r) => {
              const ok = r.test(pw);
              return (
                <li key={r.key} className={`flex items-center gap-2 text-sm ${ok ? "text-emerald-600" : "text-muted-foreground"}`}>
                  {ok ? <CheckCircle size={16} weight="fill" /> : <Circle size={16} />}
                  {r.label}
                </li>
              );
            })}
            <li className={`flex items-center gap-2 text-sm ${match ? "text-emerald-600" : "text-muted-foreground"}`}>
              {match ? <CheckCircle size={16} weight="fill" /> : <Circle size={16} />}
              Passwörter stimmen überein
            </li>
          </ul>

          {error && <p data-testid="set-password-error" className="text-sm font-medium text-danger">{error}</p>}

          <Button type="submit" data-testid="set-password-submit" disabled={loading || !allOk || !match || !current}
            className="h-12 w-full text-base font-semibold">
            {loading ? "Speichern…" : "Passwort festlegen & fortfahren"}
          </Button>
          <button type="button" onClick={() => { logout(); navigate("/login"); }}
            data-testid="set-password-cancel" className="w-full text-center text-xs text-muted-foreground hover:text-foreground">
            Abbrechen und abmelden
          </button>
        </form>
      </div>
    </div>
  );
}
